import {FC, useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';
import 'dayjs/locale/ru';
import dayjs from 'dayjs';

import Toolbar from '@mui/material/Toolbar/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import Button from '@mui/material/Button';
import {LocalizationProvider} from '@mui/x-date-pickers';
import ControlledInput from '../../../../../../../ui/controlled-input/ControlledInput';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import IconButton from '@mui/material/IconButton';

import {IContactPersonFormInputs} from '../../../../../../../shared/types/person.types';

import {emailRegExp} from '../../../../../../../shared/validation/emailRegExp';

import {
  contactPersonsToSubmitState,
  currentContactPersonIdState,
} from '../../../../../../../store/contact-person/atoms';
import {currentContactPersonToSubmitSelector} from '../../../../../../../store/contact-person/selectors';
import PhoneInput from '../../../../../../../ui/phone-input/PhoneInput';
import {Tooltip} from '@mui/material';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 2,
};

interface IContactPersonEditModal {
  open: boolean;
  handleClose: () => void;
}

const ContactPersonEditModal: FC<IContactPersonEditModal> = ({open, handleClose}) => {
  const currentContactPerson = useRecoilValue(currentContactPersonToSubmitSelector);
  const contactPersonId = useRecoilValue(currentContactPersonIdState);
  const {getValues, control, reset, trigger} = useForm<IContactPersonFormInputs>({
    defaultValues: {
      birthDate: currentContactPerson?.birthDate ? dayjs(currentContactPerson.birthDate) : null,
      firstName: currentContactPerson?.firstName,
      lastName: currentContactPerson?.lastName,
      patronymic: currentContactPerson?.patronymic,
      phoneNumber: currentContactPerson?.phoneNumber,
      email: currentContactPerson?.email,
      relationTitle: currentContactPerson?.relationTitle,
      description: currentContactPerson?.description,
    },
    mode: 'onChange',
  });
  const setContactPersons = useSetRecoilState(contactPersonsToSubmitState);
  const [dateError, setDateError] = useState<any>();

  useEffect(() => {
    return () => reset();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContactPerson]);

  const onSubmit = () => {
    const data = getValues();

    setContactPersons((prev) => {
      const index = prev.findIndex((i) => i.id === contactPersonId);

      return [
        ...prev.slice(0, index),
        {
          id: contactPersonId,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber,
          email: data.email,
          birthDate: data.birthDate?.format('YYYY-MM-DD') || '',
          relationTitle: data.relationTitle,
          description: data.description,
          patronymic: data.patronymic,
        },
        ...prev.slice(index + 1),
      ];
    });
    handleClose();
    reset();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Toolbar
          sx={{
            pl: {sm: 1},
            pr: {xs: 1, sm: 1},
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            Редактирование контактного лица
          </Typography>
        </Toolbar>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2}>
            <ControlledInput
              control={control}
              name="lastName"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              size="small"
              label="Фамилия"
              data-testid="lastName"
              data-item="1"
            />
            <ControlledInput
              control={control}
              name="firstName"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              size="small"
              label="Имя"
              data-testid="firstName"
            />
            <ControlledInput
              control={control}
              name="patronymic"
              size="small"
              label="Отчество"
              data-testid="patronymic"
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Stack direction="row">
              <PhoneInput
                control={control}
                name="phoneNumber"
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                    message: 'Некорректный номер или добавочный',
                  },
                }}
                size="small"
                label="Номер телефона"
              />
              <Tooltip
                title={
                  <>
                    <Typography variant="subtitle1">
                      Формат номера:<em>{' +7 (ххх) ххх-хх-хх'}</em>
                    </Typography>
                    <Typography variant="subtitle1">
                      Формат возможного добавочного:<em>{' #хххх'}</em>
                    </Typography>
                    <Typography variant="subtitle1">
                      Итого:<em>{' +7 (ххх) ххх-хх-хх  #хххх'}</em>
                    </Typography>
                  </>
                }
              >
                <IconButton>
                  <InfoOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <ControlledInput
              control={control}
              name="email"
              rules={{
                required: 'Обязательно для заполнения',
                pattern: {
                  value: emailRegExp,
                  message: 'Некорректный email',
                },
              }}
              size="small"
              label="Email"
              data-testid="email"
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <ControlledInput
              control={control}
              name="relationTitle"
              rules={{
                required: 'Обязательно для заполнения',
                maxLength: {
                  value: 255,
                  message: 'Максимум 255 символов',
                },
              }}
              size="small"
              label="Должность"
              data-testid="relationTitle"
            />
            <Controller
              control={control}
              name="birthDate"
              render={({field: {value, onChange}}) => (
                <LocalizationProvider adapterLocale="ru" dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Дата рождения"
                    format="YYYY.MM.DD"
                    value={value}
                    onError={(reason) => setDateError(reason)}
                    disableFuture
                    onChange={onChange}
                    // renderInput={(params) => (
                    //     <TextField
                    //         {...params}
                    //         size="small"
                    //         inputProps={{
                    //             'data-testid': "date",
                    //             ...params.inputProps,
                    //             placeholder: 'гггг.мм.дд'
                    //         }}
                    //     />
                    // )}
                  />
                </LocalizationProvider>
              )}
            />
          </Stack>
          <ControlledInput
            control={control}
            name="description"
            rules={{
              required: 'Обязательно для заполнения',
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            size="small"
            multiline
            label="Зона ответственности"
            additionalFunc={() => {
              trigger('description');
            }}
            data-testid="descriptionContactPerson"
          />
          <Stack direction="row" sx={{justifyContent: 'center'}}>
            <Button
              variant="contained"
              disabled={!!dateError}
              onClick={() => {
                trigger().then((bool) => {
                  bool && onSubmit();
                });
              }}
            >
              изменить
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default ContactPersonEditModal;
